import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MASCOT_URL = 'https://customer-assets.emergentagent.com/job_7b59aa73-04f7-469e-b3b0-3b5db031400b/artifacts/0zxv9crx_02D61451-5FF5-40EF-9C50-17F42BA59024.png';

const SUGGESTED_QUESTIONS = [
  "Pourquoi le ciel est bleu ?",
  "Pourquoi on meurt ?",
  "Pourquoi il faut aller à l'école ?"
];

const LandingPage = () => {
  const navigate = useNavigate();

  const [childName, setChildName] = useState('');
  const [question, setQuestion] = useState('');
  const [demoStep, setDemoStep] = useState('input');
  const [aiResponse, setAiResponse] = useState('');
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [hasUsedDemo, setHasUsedDemo] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(null);
  const [feedbackResponse, setFeedbackResponse] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [showSignupPopup, setShowSignupPopup] = useState(false);
  const quizCtaRef = useRef(null);

  // Loading state
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');

  const encouragingMessages = [
    "Super question ! Je réfléchis...",
    "Laisse-moi réfléchir !",
    "Je prépare une réponse parfaite...",
    "Ooh ! Intéressant !",
    "Bonne question ! Je cherche..."
  ];

  useEffect(() => {
    const demoUsed = sessionStorage.getItem('dismaman_demo_used');
    if (demoUsed === 'true') setHasUsedDemo(true);
  }, []);

  // Scroll reveal for sections
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.15 }
    );
    document.querySelectorAll('.section-reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [demoStep, hasUsedDemo]);

  // Loading animation
  useEffect(() => {
    if (demoStep !== 'loading') return;
    setLoadingProgress(0);
    setLoadingMessage(encouragingMessages[Math.floor(Math.random() * encouragingMessages.length)]);

    const msgInterval = setInterval(() => {
      setLoadingMessage(encouragingMessages[Math.floor(Math.random() * encouragingMessages.length)]);
    }, 3500);

    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => prev >= 90 ? 90 : prev + Math.random() * 12);
    }, 800);

    return () => { clearInterval(msgInterval); clearInterval(progressInterval); };
  }, [demoStep]);

  const displayName = childName || 'votre enfant';

  const generateResponse = async (questionText) => {
    if (hasUsedDemo) { setDemoStep('signup'); return; }

    setQuestion(questionText);
    setDemoStep('loading');

    try {
      const response = await axios.post(`${API_URL}/api/demo/ask`, {
        question: questionText,
        child_name: childName || 'votre enfant',
        child_age: 5
      }, { timeout: 120000 });

      setAiResponse(response.data.answer);
      setQuizQuestions(response.data.quiz_questions || []);
      sessionStorage.setItem('dismaman_demo_used', 'true');
      sessionStorage.setItem('dismaman_demo_question', questionText);
      sessionStorage.setItem('dismaman_demo_child_name', childName);
      setHasUsedDemo(true);
      setDemoStep('response');
    } catch (error) {
      console.error('Erreur demo:', error);
      setAiResponse(getFallbackResponse(questionText));
      setQuizQuestions(getFallbackQuiz(questionText));
      sessionStorage.setItem('dismaman_demo_used', 'true');
      sessionStorage.setItem('dismaman_demo_question', questionText);
      sessionStorage.setItem('dismaman_demo_child_name', childName);
      setHasUsedDemo(true);
      setDemoStep('response');
    }
  };

  const getFallbackResponse = (q) => {
    const responses = {
      "Pourquoi le ciel est bleu ?": "Super question ! Le soleil envoie de la lumière vers la Terre, et cette lumière est faite de toutes les couleurs de l'arc-en-ciel mélangées. Quand la lumière traverse l'air autour de la Terre, les petites particules dans l'air font rebondir surtout la couleur bleue dans tous les sens. C'est pour ça que quand tu regardes le ciel, tu vois du bleu partout !",
      "Pourquoi on meurt ?": "C'est une très belle question ! Tous les êtres vivants ont un cycle de vie : ils naissent, grandissent, et un jour leur corps s'arrête de fonctionner. C'est comme une fleur qui pousse, fait de belles fleurs, puis fane. Mais tu sais quoi ? Les personnes qu'on aime restent toujours dans notre cœur, dans nos souvenirs et dans les histoires qu'on raconte.",
      "Pourquoi il faut aller à l'école ?": "L'école, c'est comme un grand terrain d'aventures pour ton cerveau ! Tu y apprends à lire pour découvrir des histoires magiques, à compter pour partager équitablement des bonbons, et plein d'autres choses qui t'aideront quand tu seras grand. En plus, tu te fais des copains !"
    };
    return responses[q] || responses["Pourquoi le ciel est bleu ?"];
  };

  const getFallbackQuiz = (q) => {
    const quizzes = {
      "Pourquoi le ciel est bleu ?": [
        { question: "De quoi est faite la lumiere du soleil ?", options: ["Seulement de bleu", "De toutes les couleurs de l'arc-en-ciel", "De jaune uniquement"], correct: 1 },
        { question: "Qu'est-ce qui fait rebondir la couleur bleue ?", options: ["Les nuages", "Les petites particules dans l'air", "Les oiseaux"], correct: 1 },
        { question: "Pourquoi voit-on du bleu partout dans le ciel ?", options: ["Parce qu'on a peint le ciel", "Parce que le bleu rebondit dans tous les sens", "Parce que c'est la nuit"], correct: 1 }
      ],
      "Pourquoi on meurt ?": [
        { question: "Que font tous les etres vivants ?", options: ["Ils restent toujours pareils", "Ils naissent, grandissent et un jour s'arretent", "Ils disparaissent tout de suite"], correct: 1 },
        { question: "Comment peut-on garder les gens qu'on aime avec nous ?", options: ["En les mettant dans une boîte", "Dans notre coeur et nos souvenirs", "On ne peut pas"], correct: 1 },
        { question: "Qu'est-ce qui est important selon l'histoire ?", options: ["Avoir beaucoup de jouets", "Profiter des moments avec ceux qu'on aime", "Regarder la télé"], correct: 1 }
      ],
      "Pourquoi il faut aller à l'école ?": [
        { question: "À quoi sert d'apprendre à lire ?", options: ["À rien", "À découvrir des histoires", "À regarder des images"], correct: 1 },
        { question: "Que sont les choses qu'on apprend à l'école ?", options: ["Des devoirs ennuyeux", "Des super-pouvoirs pour la vie", "Des punitions"], correct: 1 },
        { question: "Qu'est-ce qu'on trouve aussi à l'école ?", options: ["Seulement des livres", "Des copains", "Des bonbons gratuits"], correct: 1 }
      ]
    };
    return quizzes[q] || quizzes["Pourquoi le ciel est bleu ?"];
  };

  const goToQuiz = () => { setDemoStep('quiz'); setCurrentQuizIndex(0); };

  const handleFeedback = async (type) => {
    if (feedbackGiven) return;
    setFeedbackGiven(type);
    if (type === 'understood') return; // Compris = pas de nouvelle génération

    setFeedbackLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/demo/feedback`, {
        feedback_type: type,
        original_question: question,
        original_response: aiResponse,
        child_name: childName || 'votre enfant',
        child_age: 5
      }, { timeout: 120000 });
      setFeedbackResponse(response.data.answer);
    } catch (error) {
      console.error('Erreur feedback:', error);
      setFeedbackResponse(
        type === 'too_hard'
          ? "Laisse moi t'expliquer autrement ! Imagine que la lumière du soleil, c'est comme un arc-en-ciel caché. Quand elle arrive dans le ciel, le bleu est la couleur qui rebondit le plus partout. C'est pour ça que tu vois du bleu quand tu lèves les yeux !"
          : "Aah tu veux en savoir plus, super ! En fait, ce phénomène s'appelle la diffusion de Rayleigh. Les molécules d'air sont tellement petites qu'elles diffusent surtout les courtes longueurs d'onde (le bleu et le violet). Nos yeux voient mieux le bleu, c'est pour ça que le ciel nous paraît bleu !"
      );
    } finally {
      setFeedbackLoading(false);
    }
  };

  const answerQuiz = (answerIndex) => {
    setQuizAnswers(prev => ({ ...prev, [currentQuizIndex]: answerIndex }));
    setTimeout(() => {
      quizCtaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 300);
  };

  const goToSignup = () => {
    localStorage.setItem('dismaman_pending_question', question);
    localStorage.setItem('dismaman_pending_child_name', childName);
    if (aiResponse) localStorage.setItem('dismaman_pending_response', aiResponse);
    if (quizQuestions.length > 0) localStorage.setItem('dismaman_pending_quiz', JSON.stringify(quizQuestions));
    navigate('/register');
  };

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
        @keyframes breathe {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 15px rgba(74,144,217,0.3); }
          50% { transform: scale(1.08); box-shadow: 0 8px 30px rgba(74,144,217,0.5); }
        }
        .cta-breathe { animation: breathe 2s ease-in-out infinite; }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .demo-fade-in > * {
          opacity: 0;
          animation: fadeSlideUp 0.5s ease-out forwards;
        }
        .demo-fade-in > *:nth-child(1) { animation-delay: 0.05s; }
        .demo-fade-in > *:nth-child(2) { animation-delay: 0.12s; }
        .demo-fade-in > *:nth-child(3) { animation-delay: 0.2s; }
        .demo-fade-in > *:nth-child(4) { animation-delay: 0.28s; }
        .demo-fade-in > *:nth-child(5) { animation-delay: 0.36s; }
        .demo-fade-in > *:nth-child(6) { animation-delay: 0.44s; }
        .demo-fade-in > *:nth-child(7) { animation-delay: 0.52s; }
        .demo-fade-in > *:nth-child(8) { animation-delay: 0.6s; }
        .section-reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        .section-reveal.visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>

      <div
        className="min-h-screen flex flex-col relative overflow-x-hidden"
        style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}
      >
        {/* ===== HEADER : Bulle Dis Maman + Mascotte (identique ChatPage) ===== */}
        <div className="absolute left-4 sm:left-8 top-4 sm:top-6 z-20">
          <div className="relative">
            <svg viewBox="0 0 220 90" className="w-[140px] sm:w-[180px] h-auto" style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))' }}>
              <path
                d="M 20,45 C 20,20 40,8 80,8 C 120,5 160,10 190,20 C 210,28 215,45 210,60 C 205,75 180,82 140,85 C 100,88 60,85 35,75 L 15,88 L 25,70 C 18,65 20,55 20,45 Z"
                fill="white" stroke="#374151" strokeWidth="3"
              />
            </svg>
            <p
              className="absolute top-1/2 left-1/2 text-2xl sm:text-3xl font-bold text-gray-800 whitespace-nowrap"
              style={{ fontFamily: '"Caveat", cursive', transform: 'translate(-52%, -45%) rotate(-1deg)' }}
            >
              Dis Maman !
            </p>
          </div>
        </div>

        <div className="absolute right-0 top-0 z-0 pointer-events-none">
          <img
            src={MASCOT_URL}
            alt="Alex - Mascotte Dis Maman"
            className="h-[200px] sm:h-[300px] md:h-[400px] object-contain opacity-90"
            style={{ filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.2))' }}
          />
        </div>

        {/* ===== MAIN CONTENT ===== */}
        <div className="w-full px-4 flex-1 mt-28 sm:mt-36 relative z-10">

          {/* ===== TITRES ===== */}
          <div className="text-center mb-5 px-2">
            <h1
              className="text-3xl sm:text-4xl font-bold text-gray-800 leading-snug mb-3"
              style={{ fontFamily: '"Caveat", cursive' }}
            >
              Essayez gratuitement,
              <br />sans créer de compte
            </h1>
            <p
              className="text-lg sm:text-xl text-gray-700 leading-relaxed"
              style={{ fontFamily: '"Caveat", cursive' }}
            >
              Parce que chaque &laquo; Pourquoi ? &raquo; de votre enfant
              <br />mérite un vrai moment magique.
            </p>
          </div>

          {/* ===== DEMO INPUT (style ChatPage) ===== */}
          {demoStep === 'input' && (
            <div className="max-w-md mx-auto space-y-4 demo-fade-in">
              {/* 1. Instruction + Zone de texte question */}
              <p className="text-[#333333] font-bold text-sm sm:text-base text-center">
                Posez la question de {displayName} ici :
              </p>

              <div className="relative">
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ex: Pourquoi le ciel est bleu ?"
                  className={`w-full h-28 p-4 rounded-2xl focus:outline-none resize-none shadow-md bg-white transition-colors ${
                    hasUsedDemo
                      ? 'border-2 border-gray-200 bg-gray-50 text-gray-400'
                      : 'border-[3px] border-[#5BBFBD] focus:border-[#4A90A4]'
                  }`}
                  style={{ fontSize: '16px' }}
                  data-testid="demo-question-input"
                  disabled={hasUsedDemo}
                />
              </div>

              {/* Suggestions rapides */}
              {!hasUsedDemo && (
                <div className="flex flex-wrap gap-2 justify-center">
                  {SUGGESTED_QUESTIONS.map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => setQuestion(q)}
                      className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                        question === q
                          ? 'bg-[#5BBFBD] text-white shadow-md'
                          : 'bg-white/80 text-[#4A90A4] border border-[#5BBFBD]/40 hover:bg-white hover:shadow-md'
                      }`}
                      data-testid={`demo-suggestion-${idx}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* 2. Prénom de l'enfant (même style que la question) */}
              <p className="text-[#333333] font-bold text-sm sm:text-base text-center" style={{ fontFamily: '"Caveat", cursive', fontSize: '1.1rem' }}>
                Comment s'appelle-t-il ?
              </p>
              <input
                type="text"
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
                placeholder="Prénom de votre enfant"
                className={`w-full px-4 py-3 rounded-2xl focus:outline-none shadow-md bg-white transition-colors ${
                  hasUsedDemo
                    ? 'border-2 border-gray-200 bg-gray-50 text-gray-400'
                    : 'border-[3px] border-[#5BBFBD] focus:border-[#4A90A4]'
                }`}
                style={{ fontSize: '16px' }}
                data-testid="demo-child-name"
                disabled={hasUsedDemo}
              />

              {/* Bouton envoyer (style enfant ChatPage) */}
              {!hasUsedDemo ? (
                <div className="text-center pt-2">
                  <p
                    className="text-[#333333] font-bold text-center mb-3"
                    style={{ fontFamily: '"Caveat", cursive', fontSize: '1.3rem' }}
                  >
                    {question.trim() ? `C'est parti pour ${displayName} !` : ''}
                  </p>
                  <button
                    onClick={() => question.trim() && generateResponse(question)}
                    disabled={!question.trim()}
                    className={`flex flex-col items-center mx-auto transition-all duration-200 ${
                      !question.trim() ? 'opacity-40 cursor-not-allowed' : 'hover:scale-110 active:scale-95 cursor-pointer'
                    }`}
                    data-testid="demo-generate-btn"
                  >
                    <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center text-white ${
                        question.trim() ? 'cta-breathe' : 'shadow-md'
                      }`}
                      style={{ background: question.trim() ? 'linear-gradient(135deg, #4A90D9 0%, #5BBFBD 100%)' : '#D1D5DB' }}
                    >
                      <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current" style={{ transform: 'rotate(-15deg)' }}>
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                      </svg>
                    </div>
                    <span
                      className="mt-2 text-[#333333] font-semibold"
                      style={{ fontFamily: '"Caveat", cursive', fontSize: '1.4rem' }}
                    >
                      {childName || 'Voir la réponse 🌟'}
                    </span>
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-amber-700 font-medium text-sm mb-3 bg-amber-50 rounded-xl px-4 py-2 inline-block">
                    Vous avez déjà essayé la démo !
                  </p>
                  <button
                    onClick={goToSignup}
                    className="w-full py-4 rounded-2xl font-bold text-lg text-white shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{ background: 'linear-gradient(135deg, #4A90D9 0%, #5BBFBD 100%)' }}
                    data-testid="demo-signup-btn"
                  >
                    Créez votre compte gratuit pour continuer
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ===== LOADING (style ThinkingPopup) ===== */}
          {demoStep === 'loading' && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, rgba(184,212,227,0.95) 0%, rgba(167,211,216,0.95) 25%, rgba(158,205,196,0.95) 50%, rgba(168,213,186,0.95) 75%, rgba(181,220,174,0.95) 100%)' }}>
              <div className="relative max-w-sm w-full">
                <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 z-10">
                  <div className="relative">
                    <img
                      src={MASCOT_URL}
                      alt="Maman reflechit"
                      className="h-32 w-auto object-contain"
                      style={{ filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.2))' }}
                    />
                    <div className="absolute -top-2 right-0">
                      <div className="w-3 h-3 bg-white rounded-full animate-bounce shadow-md" style={{ animationDelay: '0s' }}></div>
                    </div>
                    <div className="absolute -top-6 right-4">
                      <div className="w-4 h-4 bg-white rounded-full animate-bounce shadow-md" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <div className="absolute -top-12 right-6">
                      <div className="w-6 h-6 bg-white rounded-full animate-bounce shadow-md" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/90 backdrop-blur-md rounded-3xl p-6 pt-16 shadow-2xl border border-white/50">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-800 mb-1" style={{ fontFamily: '"Caveat", cursive' }}>
                      {loadingMessage}
                    </p>
                    <p className="text-sm text-teal-600 font-medium mb-5">
                      Je prépare une réponse parfaite...
                    </p>

                    <div className="mb-5">
                      <div className="w-full bg-gray-200/80 rounded-full h-3 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${Math.min(loadingProgress, 95)}%`, background: 'linear-gradient(90deg, #3B82F6 0%, #14B8A6 100%)' }}
                        ></div>
                      </div>
                    </div>

                    <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-teal-200/50">
                      <p className="text-sm text-teal-700 mb-1 font-medium" style={{ fontFamily: '"Caveat", cursive', fontSize: '1rem' }}>
                        La question de {displayName} :
                      </p>
                      <p className="text-gray-700" style={{ fontFamily: '"Caveat", cursive', fontSize: '1.1rem' }}>
                        &laquo; {question} &raquo;
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===== RESPONSE (style bulles ChatPage) ===== */}
          {demoStep === 'response' && (
            <div className="max-w-md mx-auto space-y-4">
              {/* Question - bulle teal (gauche, style app) */}
              <div className="flex justify-start">
                <div
                  className="max-w-[85%] px-4 py-3 rounded-2xl shadow-md text-white"
                  style={{ background: 'linear-gradient(135deg, #4FBDBA 0%, #5BBFBD 100%)' }}
                >
                  <p className="text-base leading-relaxed">{question}</p>
                </div>
              </div>

              {/* Reponse IA - bulle blanche (droite, style app) */}
              <div className="flex justify-end">
                <div className="max-w-[85%] px-4 py-3 rounded-2xl shadow-md bg-white text-[#333333]">
                  <p className="text-base leading-relaxed whitespace-pre-wrap">{aiResponse}</p>
                </div>
              </div>

              {/* Feedback buttons - Cartes carrées, emoji au-dessus (style app) */}
              <div className={`grid grid-cols-3 gap-2 ${feedbackGiven ? 'opacity-60 pointer-events-none' : ''}`}>
                <button
                  onClick={() => handleFeedback('too_hard')}
                  className={`flex flex-col items-center justify-center py-3 px-2 rounded-2xl transition-all duration-200 bg-[#FFF5E6] hover:bg-[#FFEDD6] cursor-pointer active:scale-95 ${
                    feedbackGiven === 'too_hard' ? 'ring-2 ring-[#E67E22] !opacity-100' : ''
                  }`}
                  data-testid="demo-feedback-hard"
                >
                  <span className="text-2xl mb-1">🤔</span>
                  <span className="text-[#E67E22] text-sm font-semibold leading-tight text-center">Pas<br/>compris</span>
                </button>
                <button
                  onClick={() => handleFeedback('understood')}
                  className={`flex flex-col items-center justify-center py-3 px-2 rounded-2xl transition-all duration-200 shadow-md text-white hover:opacity-90 cursor-pointer active:scale-95 ${
                    feedbackGiven === 'understood' ? 'ring-2 ring-white !opacity-100' : ''
                  }`}
                  style={{ background: 'linear-gradient(135deg, #4CAF50 0%, #45B7AA 100%)' }}
                  data-testid="demo-feedback-understood"
                >
                  <span className="text-2xl mb-1">👍</span>
                  <span className="text-sm font-semibold">Compris</span>
                </button>
                <button
                  onClick={() => handleFeedback('more_info')}
                  className={`flex flex-col items-center justify-center py-3 px-2 rounded-2xl transition-all duration-200 bg-[#F0F8FF] border border-[#87CEEB] hover:bg-[#E0F0FF] cursor-pointer active:scale-95 ${
                    feedbackGiven === 'more_info' ? 'ring-2 ring-[#87CEEB] !opacity-100' : ''
                  }`}
                  data-testid="demo-feedback-more"
                >
                  <span className="text-2xl mb-1">🧐</span>
                  <span className="text-[#4A90A4] text-sm font-semibold leading-tight text-center">En savoir<br/>+</span>
                </button>
              </div>

              {/* Hint text */}
              {!feedbackGiven && (
                <p className="text-center text-sm text-[#9ECDC4] italic" style={{ fontFamily: '"Caveat", cursive' }}>
                  Aidez Dis Maman à mieux répondre la prochaine fois
                </p>
              )}

              {/* Réponse adaptée (feedback too_hard ou more_info) */}
              {feedbackLoading && (
                <div className="flex justify-end">
                  <div className="max-w-[85%] px-4 py-3 rounded-2xl shadow-md bg-white/80 text-[#333333]">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-[#5BBFBD] rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                        <div className="w-2 h-2 bg-[#5BBFBD] rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                        <div className="w-2 h-2 bg-[#5BBFBD] rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                      </div>
                      <span className="text-sm text-gray-500">
                        {feedbackGiven === 'too_hard' ? 'Je reformule...' : 'Je cherche plus d\'infos...'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              {feedbackResponse && (
                <div className="flex justify-end">
                  <div className="max-w-[85%] px-4 py-3 rounded-2xl shadow-md bg-white text-[#333333]">
                    <p className="text-base leading-relaxed whitespace-pre-wrap">{feedbackResponse}</p>
                  </div>
                </div>
              )}

              {/* Bouton quiz - FULL WIDTH (style app violet) */}
              <button
                onClick={goToQuiz}
                className="w-full flex items-center justify-center gap-3 py-4 rounded-full text-white font-semibold text-lg shadow-md hover:opacity-90 transition-all duration-200 active:scale-95"
                style={{ background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)' }}
                data-testid="demo-quiz-btn"
              >
                <span className="text-xl text-red-300">❓</span>
                <span>Lancer le quiz</span>
              </button>

              {/* CTA inscription */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center">
                <p
                  className="text-[#333333] font-bold mb-2"
                  style={{ fontFamily: '"Caveat", cursive', fontSize: '1.2rem' }}
                >
                  Ça vous plaît ?
                </p>
                <p className="text-gray-600 text-sm mb-3">
                  Inscrivez-vous gratuitement pour poser d'autres questions !
                </p>
                <button
                  onClick={goToSignup}
                  className="w-full py-3 rounded-full font-semibold text-white shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #4A90D9 0%, #5BBFBD 100%)' }}
                  data-testid="demo-signup-response-btn"
                >
                  Créer mon compte gratuit
                </button>
              </div>
            </div>
          )}

          {/* ===== QUIZ - IDENTIQUE à QuizPage.js ===== */}
          {demoStep === 'quiz' && quizQuestions.length > 0 && (
            <div className="max-w-md mx-auto">
              {/* Header du quiz avec compteur et progress bars */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-3 mb-4 flex items-center justify-between">
                <span className="text-[#333333] font-medium">
                  Question {currentQuizIndex + 1}/{quizQuestions.length}
                </span>

                {/* Progress bars (style app - barres épaisses, pas des dots) */}
                <div className="flex gap-1.5">
                  {quizQuestions.map((_, idx) => (
                    <div
                      key={idx}
                      className={`w-10 h-2.5 rounded-full transition-all ${
                        idx < currentQuizIndex ? 'bg-[#4CAF50]'
                          : idx === currentQuizIndex ? 'bg-[#9B59B6]' : 'bg-[#E0E0E0]'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Carte de question - Style app mobile */}
              <div className="bg-white rounded-3xl p-6 shadow-md mb-4">
                <p
                  className="text-xl sm:text-2xl text-[#333333] text-center leading-relaxed font-medium"
                  style={{ fontFamily: '"Caveat", cursive' }}
                >
                  {quizQuestions[currentQuizIndex]?.question}
                </p>
              </div>

              {/* Boutons de réponse - Style app mobile */}
              <div className="space-y-3 mb-4">
                {quizQuestions[currentQuizIndex]?.options?.map((option, idx) => {
                  const answered = quizAnswers[currentQuizIndex] !== undefined;
                  const isCorrect = idx === quizQuestions[currentQuizIndex].correct;
                  const isSelected = quizAnswers[currentQuizIndex] === idx;

                  let buttonStyle = 'bg-white border-gray-200 text-[#333333]';
                  let icon = null;

                  if (answered) {
                    if (isCorrect) {
                      buttonStyle = 'bg-[#E8F5E9] border-[#4CAF50] text-[#2E7D32] font-semibold';
                      icon = <span className="text-[#4CAF50] text-xl">✓</span>;
                    } else if (isSelected) {
                      buttonStyle = 'bg-[#FFF3E0] border-[#FFAB91] text-[#E64A19] opacity-60';
                      icon = <span className="text-[#E64A19] text-xl">✗</span>;
                    }
                  }

                  return (
                    <button
                      key={idx}
                      onClick={() => !answered && answerQuiz(idx)}
                      disabled={answered}
                      className={`w-full p-4 rounded-2xl border-2 flex justify-between items-center transition-all duration-200 ${buttonStyle} ${
                        !answered ? 'hover:border-[#4A90A4] hover:bg-gray-50 cursor-pointer active:scale-[0.98]' : 'cursor-default'
                      }`}
                      data-testid={`demo-quiz-option-${idx}`}
                    >
                      <span className="text-left">
                        <span className="font-bold mr-1">{String.fromCharCode(65 + idx)}.</span>
                        {option}
                      </span>
                      {icon}
                    </button>
                  );
                })}
              </div>

              {/* Bouton suivant après réponse - Style violet app */}
              {quizAnswers[currentQuizIndex] !== undefined && (
                <button
                  onClick={() => {
                    if (currentQuizIndex < quizQuestions.length - 1) {
                      setCurrentQuizIndex(prev => prev + 1);
                    } else {
                      setShowSignupPopup(true);
                    }
                  }}
                  className="w-full py-4 rounded-full text-white font-semibold text-lg shadow-md hover:opacity-90 transition-all active:scale-[0.98]"
                  style={{ background: 'linear-gradient(135deg, #7EC8E3 0%, #5BBFBD 100%)' }}
                  data-testid="demo-quiz-next"
                >
                  {currentQuizIndex < quizQuestions.length - 1 ? 'Question suivante →' : '🎉 Terminer'}
                </button>
              )}

              {/* CTA inscription sous chaque question du quiz */}
              {quizAnswers[currentQuizIndex] !== undefined && (
                <div ref={quizCtaRef} className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center mt-2">
                  <p
                    className="text-[#333333] font-bold mb-2"
                    style={{ fontFamily: '"Caveat", cursive', fontSize: '1.2rem' }}
                  >
                    Ça vous plaît ?
                  </p>
                  <p className="text-gray-600 text-sm mb-3">
                    Inscrivez-vous gratuitement pour poser d'autres questions !
                  </p>
                  <button
                    onClick={goToSignup}
                    className="w-full py-3 rounded-full font-semibold text-white shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{ background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)' }}
                    data-testid="demo-signup-quiz-btn"
                  >
                    Créer mon compte gratuit
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ===== POPUP SIGNUP (dernière question quiz) ===== */}
          {showSignupPopup && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: 'rgba(0,0,0,0.5)' }}>
              <div className="bg-white rounded-3xl p-6 shadow-2xl max-w-sm w-full text-center relative">
                <button
                  onClick={() => setShowSignupPopup(false)}
                  className="absolute top-3 right-4 text-gray-400 hover:text-gray-600 text-2xl"
                  data-testid="popup-close"
                >
                  ×
                </button>
                <div className="relative w-20 h-20 mx-auto mb-4" style={{ animation: 'float 3s ease-in-out infinite' }}>
                  <img src={MASCOT_URL} alt="Mascotte" className="w-full h-full object-contain" />
                </div>
                <h2
                  className="text-2xl font-bold text-[#333333] mb-2"
                  style={{ fontFamily: '"Caveat", cursive' }}
                >
                  Bravo ! 🎉
                </h2>
                <p
                  className="text-lg text-[#333333] mb-3"
                  style={{ fontFamily: '"Caveat", cursive' }}
                >
                  Ça vous plaît ?
                </p>
                <p className="text-gray-600 text-sm mb-5 leading-relaxed">
                  Inscrivez-vous gratuitement pour poser d'autres questions
                  {childName ? ` à ${childName}` : ''} — sans carte bancaire, en 30 secondes.
                </p>
                <button
                  onClick={goToSignup}
                  className="w-full py-4 rounded-full font-bold text-lg text-white shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98] mb-3"
                  style={{ background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)' }}
                  data-testid="popup-signup-btn"
                >
                  Créer mon compte gratuit
                </button>
                <button
                  onClick={() => setShowSignupPopup(false)}
                  className="text-gray-500 text-sm hover:text-gray-700"
                >
                  Peut-être plus tard
                </button>
              </div>
            </div>
          )}

          {/* ===== SIGNUP CTA ===== */}
          {demoStep === 'signup' && (
            <div className="max-w-md mx-auto">
              <div className="bg-white/90 backdrop-blur-md rounded-3xl p-6 shadow-2xl text-center">
                <div className="relative w-20 h-20 mx-auto mb-4" style={{ animation: 'float 3s ease-in-out infinite' }}>
                  <img src={MASCOT_URL} alt="Mascotte" className="w-full h-full object-contain" />
                </div>

                <h2
                  className="text-2xl font-bold text-[#333333] mb-3"
                  style={{ fontFamily: '"Caveat", cursive' }}
                >
                  Vous avez aimé ?
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Créez votre compte gratuit pour poser une question par jour
                  {childName ? ` à ${childName}` : ''} — sans carte bancaire, en 30 secondes.
                </p>

                <button
                  onClick={goToSignup}
                  className="w-full py-4 rounded-2xl font-bold text-lg text-white shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98] mb-3"
                  style={{ background: 'linear-gradient(135deg, #4A90D9 0%, #5BBFBD 100%)' }}
                  data-testid="demo-signup-btn"
                >
                  Créer mon compte gratuit
                </button>

                <p className="text-sm text-gray-500">
                  Déjà un compte ?{' '}
                  <Link to="/login" className="text-[#4A90A4] font-medium underline">Se connecter</Link>
                </p>
              </div>
            </div>
          )}

          {/* ===== SECTIONS BELOW DEMO (only on input step) ===== */}
          {demoStep === 'input' && !hasUsedDemo && (
            <>
              {/* Comment ca marche */}
              <div className="max-w-md mx-auto mt-8 bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-md section-reveal">
                <h2
                  className="text-xl font-bold text-center text-[#333333] mb-6"
                  style={{ fontFamily: '"Caveat", cursive' }}
                >
                  Comment ça marche ?
                </h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-white shadow-md"
                      style={{ background: 'linear-gradient(135deg, #4A90D9 0%, #5BBFBD 100%)' }}
                    >
                      <span className="font-bold text-lg">1</span>
                    </div>
                    <div>
                      <p className="text-[#333333] font-bold">Votre enfant pose une question</p>
                      <p className="text-gray-500 text-sm italic">"Dis Maman, pourquoi les étoiles brillent ?"</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-white shadow-md"
                      style={{ background: 'linear-gradient(135deg, #4ECDC4 0%, #45B7AA 100%)' }}
                    >
                      <span className="font-bold text-lg">2</span>
                    </div>
                    <div>
                      <p className="text-[#333333] font-bold">Vous la tapez dans Dis Maman</p>
                      <p className="text-gray-500 text-sm italic">En 2 secondes, depuis votre téléphone</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-white shadow-md"
                      style={{ background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)' }}
                    >
                      <span className="font-bold text-lg">3</span>
                    </div>
                    <div>
                      <p className="text-[#333333] font-bold">Vous lisez la réponse ensemble</p>
                      <p className="text-gray-500 text-sm italic">Un vrai moment de complicité parent-enfant</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mémorisation */}
              <div className="max-w-md mx-auto mt-6 bg-white/70 backdrop-blur-sm rounded-3xl p-6 shadow-md border border-purple-100/50 section-reveal">
                <h2
                  className="text-xl font-bold text-center text-[#333333] mb-3"
                  style={{ fontFamily: '"Caveat", cursive' }}
                >
                  Et si votre enfant se souvenait vraiment ?
                </h2>
                <p className="text-gray-600 text-center mb-4 text-sm leading-relaxed">
                  Dis Maman re-présente les questions au bon moment pour ancrer le savoir durablement.
                  Comme des révisions ludiques, sans effort.
                </p>
                <div className="flex justify-center gap-6 text-center">
                  <div>
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-1 text-white shadow-md"
                      style={{ background: 'linear-gradient(135deg, #4A90D9 0%, #5BBFBD 100%)' }}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                    </div>
                    <p className="text-xs text-gray-500">Mémorisation<br />naturelle</p>
                  </div>
                  <div>
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-1 text-white shadow-md"
                      style={{ background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)' }}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </div>
                    <p className="text-xs text-gray-500">Quiz<br />amusants</p>
                  </div>
                  <div>
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-1 text-white shadow-md"
                      style={{ background: 'linear-gradient(135deg, #4ECDC4 0%, #45B7AA 100%)' }}
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                    </div>
                    <p className="text-xs text-gray-500">Savoir<br />durable</p>
                  </div>
                </div>
                <p className="text-center mt-4 text-purple-600 font-medium text-sm">
                  Disponible avec les abonnements premium
                </p>
              </div>

              {/* Témoignages placeholder */}
              <div className="max-w-md mx-auto mt-6 bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-md section-reveal">
                <h2
                  className="text-xl font-bold text-center text-[#333333] mb-4"
                  style={{ fontFamily: '"Caveat", cursive' }}
                >
                  Ce qu'en disent les parents
                </h2>
                <div className="space-y-3">
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                    <p className="text-gray-700 italic text-sm leading-relaxed">"Victor me demande lui-même de poser ses questions à l'app. Et les petits quiz… c'est devenu mon arme secrète quand il commence à s'énerver !"</p>
                    <p className="text-[#4A90A4] text-xs mt-2 font-medium">— Alexandra, maman de Victor, 4 ans</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ===== CTA + FOOTER ===== */}
          {demoStep === 'input' && (
            <div className="max-w-md mx-auto mt-6 mb-4 text-center">
              <Link
                to="/register"
                className="text-[#4A90A4]/70 text-sm hover:text-[#4A90A4] transition-colors"
                data-testid="direct-register-link"
              >
                Créer mon compte directement →
              </Link>
              <p className="text-gray-600 mt-3 mb-4">
                Déjà un compte ?{' '}
                <Link to="/login" className="text-[#4A90A4] font-bold underline hover:text-[#3A7A8A]" data-testid="login-link">
                  Se connecter
                </Link>
              </p>
              <p className="text-sm text-gray-500 flex items-center justify-center gap-2 flex-wrap">
                <span>Adapté 3-10 ans</span>
                <span>|</span>
                <span>100% sécurisé</span>
                <span>|</span>
                <span>Sans carte bancaire</span>
              </p>
            </div>
          )}

          {/* Liens legaux */}
          <div className="text-center text-sm text-gray-500 space-x-4 py-4 mt-auto">
            <Link to="/confidentialite" className="underline hover:text-gray-700">Confidentialité</Link>
            <Link to="/conditions-utilisation" className="underline hover:text-gray-700">CGU</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default LandingPage;
